import React from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';
import { useSwipeable } from 'react-swipeable';
import { RCButton } from '@reverbdotcom/cadence/components';
import { CaretLeftIcon, CaretRightIcon } from '@reverbdotcom/cadence/icons/react';

import LightboxImageItem from './lightbox_image_item';

// How the user moved to an image, so the caller can track it.
export type LightboxIndexChangeSource = 'next' | 'previous' | 'thumbnail';

export function isLink(url: string) {
  if (!url) { return false; }
  return url.slice(0, 4) === 'http';
}

export function nextIndex(index: number, count: number) {
  return index + 1 >= count ? 0 : index + 1;
}

export function previousIndex(index: number, count: number) {
  return index - 1 >= 0 ? index - 1 : count - 1;
}

interface SwipeableProps {
  onSwipedLeft?: (e: any) => void;
  onSwipedRight?: (e: any) => void;
  children: React.ReactNode;
}

// useSwipeable callbacks receive SwipeEventData (not DOM events). Wrap the
// handler so that it receives an object with a no-op stopPropagation, like the
// click handlers it shares.
const toSwipeHandler = (handler?: (e: any) => void) => {
  if (!handler) return undefined;
  return (swipeData: any) => handler({ ...swipeData, stopPropagation: () => {} });
};

export function Swipeable({ onSwipedLeft, onSwipedRight, children }: SwipeableProps) {
  const handlers = useSwipeable({
    onSwipedLeft: toSwipeHandler(onSwipedLeft),
    onSwipedRight: toSwipeHandler(onSwipedRight),
  });

  return <div {...handlers}>{children}</div>;
}

const LEFT_ARROW_CODE = 37;
const RIGHT_ARROW_CODE = 39;

interface ArrowKeyHandlers {
  onPrevious: (e) => void;
  onNext: (e) => void;
}

export function handleArrowKeyDown(e, { onPrevious, onNext }: ArrowKeyHandlers) {
  const tag = e.target.tagName;
  // don't change the slides if user is typing in an input
  if ((tag === 'TEXTAREA') || (tag === 'INPUT')) { return; }
  if (e.ctrlKey || e.metaKey || e.altKey) { return; }
  if (e.keyCode === LEFT_ARROW_CODE) { onPrevious(e); }
  if (e.keyCode === RIGHT_ARROW_CODE) { onNext(e); }
}

// Listens for arrow keys on the window while enabled, calling the latest handlers.
export function useWindowArrowKeys(enabled: boolean, handlers: ArrowKeyHandlers) {
  const handlersRef = React.useRef(handlers);
  handlersRef.current = handlers;

  React.useEffect(() => {
    if (!enabled) return undefined;

    const listener = (e: KeyboardEvent) => handleArrowKeyDown(e, handlersRef.current);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [enabled]);
}

// Scrolls a thumbnail row so the active thumbnail, and half of its neighbour, are in view.
export function scrollThumbnailRow(rowElem: HTMLElement, activeIndex: number) {
  const activeThumbElem = rowElem?.children[activeIndex];
  if (!activeThumbElem) return;

  const nextThumbElem = activeThumbElem.nextElementSibling;
  const prevThumbElem = activeThumbElem.previousElementSibling;

  const activeThumbRect = activeThumbElem.getBoundingClientRect();
  const parentRowRect = rowElem.getBoundingClientRect();

  const activeThumbElemY = activeThumbRect.x + activeThumbRect.width;
  const parentRowElemY = parentRowRect.x + parentRowRect.width;

  const nextThumbOverflowingRight = (thumb) => {
    return ((thumb.getBoundingClientRect().x + thumb.clientWidth - parentRowElemY) / thumb.clientWidth) > 0.15;
  };

  const prevThumbOverflowingLeft = (thumb) => {
    return ((parentRowRect.x - thumb.getBoundingClientRect().x) / thumb.clientWidth) > 0.15;
  };

  const activeThumbIsOverflowingRight = activeThumbElemY > parentRowElemY;
  const nextThumbIsOverflowingRight = nextThumbElem && nextThumbOverflowingRight(nextThumbElem);

  const activeThumbIsOverflowingLeft = activeThumbRect.x < parentRowRect.x;
  const prevThumbIsOverflowingLeft = prevThumbElem && prevThumbOverflowingLeft(prevThumbElem);

  const showHalfOfNextThumb = activeThumbIsOverflowingRight || nextThumbIsOverflowingRight;
  const showHalfOfPrevThumb = activeThumbIsOverflowingLeft || prevThumbIsOverflowingLeft;

  const activeThumbIsFirstThumb = activeIndex === 0;
  const activeThumbIsLastThumb = activeIndex === rowElem.children.length - 1;

  const calculateNextThumbPosition = () => {
    if (nextThumbElem.nextElementSibling) {
      return nextThumbElem.nextElementSibling.getBoundingClientRect().x + (nextThumbElem.nextElementSibling.clientWidth / 2);
    } else {
      return nextThumbElem.getBoundingClientRect().x + nextThumbElem.clientWidth;
    }
  };

  const calculatePreviousThumbPosition = () => {
    if (prevThumbElem.previousElementSibling) {
      return prevThumbElem.previousElementSibling.getBoundingClientRect().x + (prevThumbElem.previousElementSibling.clientWidth / 2);
    } else {
      return prevThumbElem.getBoundingClientRect().x;
    }
  };

  if (activeThumbIsFirstThumb) {
    rowElem.scrollLeft = 0;
  } else if (activeThumbIsLastThumb) {
    rowElem.scrollLeft = rowElem.scrollWidth;
  } else if (showHalfOfNextThumb) {
    const scrollAmount = calculateNextThumbPosition() - parentRowElemY;
    rowElem.scrollLeft += scrollAmount;
  } else if (showHalfOfPrevThumb) {
    const scrollAmount = parentRowRect.x - calculatePreviousThumbPosition();
    rowElem.scrollLeft -= scrollAmount;
  }
}

interface ThumbnailsProps {
  urls: string[];
  alts?: string[];
  activeIndex: number;
  large?: boolean;
  disableImageOverlay?: boolean;
  onThumbClick: (evt, index: number) => void;
}

// The ref points at the scrolling row of thumbnails.
export const LightboxImageThumbnails = React.forwardRef<HTMLDivElement, ThumbnailsProps>(function LightboxImageThumbnails({
  urls,
  alts,
  activeIndex,
  large,
  disableImageOverlay,
  onThumbClick,
}, ref) {
  return (
    <div className="lightbox-image__thumbs">
      <div className="lightbox-image__thumbs__inner" ref={ref}>
        {urls.map((urlOrSlug, idx) => (
          <div
            className={classNames(
              'lightbox-image__thumb',
              { 'lightbox-image__thumb--active': activeIndex === idx },
              { 'lightbox-image__thumb--large': large },
            )}
            key={idx}
          >
            <LightboxImageItem
              urlOrSlug={urlOrSlug}
              index={idx}
              alt={alts?.[idx]}
              onClick={onThumbClick}
              disableImageOverlay={disableImageOverlay}
              thumbnail
            />
          </div>
        ))}
      </div>
    </div>
  );
});

interface ControlsProps {
  size: 'small' | 'medium';
  // an arrow without a handler isn't shown, for a viewer that stops at the first or last item
  onPrevious?: (e) => void;
  onNext?: (e) => void;
}

export function LightboxImageControls({ size, onPrevious, onNext }: ControlsProps) {
  return (
    <div data-controls>
      {onNext &&
        <div className="lightbox-image__control-wrapper lightbox-image__control-wrapper--next">
          <RCButton
            variant="elevated-light"
            size={size}
            iconOnly={<CaretRightIcon weight="bold" />}
            iconLabel={I18n.t('commons.nextImage')}
            hideTooltip
            onClick={onNext}
          />
        </div>
      }
      {onPrevious &&
        <div className="lightbox-image__control-wrapper lightbox-image__control-wrapper--prev">
          <RCButton
            variant="elevated-light"
            size={size}
            iconOnly={<CaretLeftIcon weight="bold" />}
            iconLabel={I18n.t('commons.previousImage')}
            hideTooltip
            onClick={onPrevious}
          />
        </div>
      }
    </div>
  );
}
